import AttachFileIcon from '@mui/icons-material/AttachFile';
import {Box, FormControl, IconButton, Typography} from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import {useField, useFormikContext} from 'formik';
import {getFieldLabel} from 'main/utility/LayoutUtil';
import {MuiFileInput} from 'mui-file-input';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import './ImagePreview.css';
import CloseIcon from '@mui/icons-material/Close';
import {isEmpty} from 'main/utility/CommonUtil';
import {useSelector} from 'react-redux';

const AppImageUploader = (props) => {
  const {maxfilesizemb = 1, filetype = []} = props;
  const [field, meta] = useField(props);
  //const { setFieldValue } = useFormikContext();
  const {values, setFieldValue} = useFormikContext();

  const [multiple] = useState(props.multivalue);
  const [maxFiles] = useState(multiple ? props.max : 1);
  const [imagetype] = useState(props.format);

  const [files, setFiles] = useState([]);
  const hasRun = useRef(false);
  const [fileCount, setFileCount] = useState(0);

  useEffect(() => {
    if (isEmpty(field.value)) {
      return;
    }

    // if files are initially set once for edit mode
    if (!hasRun.current) {
      hasRun.current = true;

      const existingImages = Array.isArray(field.value)
        ? field.value
        : field.value.toString().split(',');

      setFileCount(existingImages.length);
      updateFileValues(
        existingImages.map((url) => ({
          type: 'url',
          src: url,
        })),
      );
    }
  }, [field.value]);

  const [internalError, setInternalError] = useState('');
  const errorText = (meta.touched && meta.error) || internalError;
  const [previews, setPreviews] = useState([]);
  const appEndPoint = useSelector(
    ({commonReducer}) => commonReducer.appEndPoint,
  );

  const isValidFile = (selectedFileType) => {
    if (filetype.includes('image/*')) {
      return selectedFileType.startsWith('image/');
    }
    return filetype.includes(selectedFileType);
  };

  const handleFileChange = (newFiles) => {
    if (!newFiles) return;
    const newFilesArray = Array.isArray(newFiles)
      ? newFiles.filter(
          (newFile) =>
            !files.some(
              (f) => f.src.name === newFile.name && f.src.size === newFile.size,
            ),
        )
      : [newFiles];

    for (let file of newFilesArray) {
      // 2. Validation
      if (file.length > 0 && !isValidFile(file.type)) {
        setInternalError(`Invalid file type. Allowed: ${filetype.join(', ')}`);
        return;
      }

      if (file.size > maxfilesizemb * 1024 * 1024) {
        setInternalError(`Max size is ${maxfilesizemb}MB.`);
        return;
      }
    }
    const formatted = newFilesArray.map((file) => ({
      type: 'file',
      src: file,
    }));

    if (!isEmpty(files) && !multiple) addRemovedFiletoFormIk(0);
    const mergedFiles = multiple ? [...files, ...formatted] : formatted;

    // enforce max files
    if (multiple && mergedFiles.length > maxFiles && maxFiles > 0) {
      setInternalError(`Maximum ${maxFiles} images allowed`);
      return;
    }

    // 3. Success Logic
    setInternalError('');
    updateFileValues(mergedFiles);
  };

  // FOR IMAGE FILE REMOVAL FROM SERVER
  const addRemovedFiletoFormIk = (index) => {
    if (files[index].type === 'url')
      setFieldValue('filesRemoved', [
        ...(values.filesRemoved || []),
        files[index].src,
      ]);
  };
  const removeImage = (index) => {
    addRemovedFiletoFormIk(index);
    if (multiple) {
      updateFileValues(files.filter((_, i) => i !== index));
    } else {
      updateFileValues([]);
    }
  };

  const updateFileValues = (value) => {
    setFiles(value);
    setFieldValue(field.name, value);
  };

  const getPreview = (item) => {
    if (item.type === 'url') return appEndPoint + '/' + item.src.slice(3);
    return URL.createObjectURL(item.src);
  };

  useEffect(() => {
    if (!files) return;

    /// ADDING 'fileInputFields' IN FORMIKCONTEXT, REQUIRED FOR FILE TYPE REQUEST
    const isFileUploaded = files.some((f) => f.type === 'file');
    if (isFileUploaded) {
      const current = values.fileInputFields || [];
      if (!current.includes(field.name)) {
        setFieldValue('fileInputFields', [...current, field.name]);
      }
    }
    setFileCount(files.length);
    // generate previews
    const urls = files.map((file) => getPreview(file));
    setPreviews(urls);

    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  return (
    <FormControl error={!!errorText} fullWidth>
      <Box sx={{position: 'relative', width: '300px'}}>
        <MuiFileInput
          multiple={multiple}
          {...field}
          value={files.src}
          label={getFieldLabel(props.label, props.required)}
          onChange={handleFileChange}
          InputProps={{startAdornment: <AttachFileIcon />}}
          inputProps={{accept: filetype?.join(',')}}
          sx={{input: {cursor: 'pointer'}}}
          maxfiles={maxFiles}
          placeholder={multiple ? 'Select images' : 'Select image'}
        />
        <Typography
          variant='subtitle2'
          sx={{
            // Absolute positioning to center it vertically over the textbox
            position: 'absolute',
            left: '40px',
            top: '55%',
            transform: 'translateY(-50%)',

            // CRUCIAL: Allows clicks to pass through this text to open the file dialog
            pointerEvents: 'none',

            // Custom styling
            backgroundColor: 'transparent',
            zIndex: 2,
          }}
        >
          {fileCount > 0
            ? `${fileCount} item${fileCount > 1 ? 's' : ''} attached`
            : 'No attachments yet.'}
        </Typography>
      </Box>
      {errorText && <FormHelperText>{errorText}</FormHelperText>}
      {/* Preview */}
      <Box className='image-preview-container'>
        {previews.map((src, i) => (
          <div key={i} className={`image-preview-box }`}>
            <div
              className={`image-preview-child-box ${
                imagetype === 'thumbnail' ? 'thumbnail' : ''
              }`}
            >
              <img key={i} src={src} alt='preview' />
            </div>
            <IconButton
              className='preview-close-btn'
              onClick={() => removeImage(i)}
            >
              <CloseIcon />
            </IconButton>
          </div>
        ))}
      </Box>
    </FormControl>
  );
};

AppImageUploader.propTypes = {
  widgetid: PropTypes.string,
  labelid: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
  multivalue: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  maxfilesizemb: PropTypes.number,
  filetype: PropTypes.array,
  max: PropTypes.number,
  format: PropTypes.string,
};

export default AppImageUploader;
